import type { Attribution } from "./attribution"
import type { GenerationResponse, ProductContext, WidgetCapabilities, WidgetConfig } from "./types"

export class ApiClient {
  private token: string | null = null
  private tokenExpiresAt = 0
  private tokenRequest: Promise<string> | null = null

  constructor(
    private readonly config: WidgetConfig,
    private readonly visitor: string,
    private readonly session: string,
    private readonly attribution: Attribution = {}
  ) {}

  async tryOn(person: File, product: ProductContext, visitor: string,
              session: string,
              onCreated?: (created: GenerationResponse) => void,
              sizing?: { heightCm: number }): Promise<GenerationResponse> {
    const form = new FormData()
    form.append("person_image", person)
    form.append("consent", "true")
    form.append("visitor", visitor)
    form.append("session", session)
    // Combined delivery: ask the try-on call to also return a size verdict. The
    // server only honors it when the account actually seizes the call.
    if (sizing) {
      form.append("include_size_recommendation", "true")
      form.append("height_cm", String(sizing.heightCm))
    }
    appendProduct(form, product)
    return this.createAndPoll("/api/v1/try_on", form, onCreated)
  }

  // Poll an already-created generation by id until it is terminal. Bounded by a
  // hard wall-clock budget (default 30s from the shopper's click): beyond that the
  // wait is not acceptable UX, so we give up and let the caller surface a retry —
  // the server-side reaper independently reclaims the row.
  static readonly POLL_BUDGET_MS = 30_000
  private static readonly TERMINAL = [ "succeeded", "failed", "timed_out", "cancelled" ]

  async pollGeneration(id: string | number,
                       budgetMs = ApiClient.POLL_BUDGET_MS): Promise<GenerationResponse> {
    const deadline = Date.now() + budgetMs
    while (Date.now() < deadline) {
      const generation = await this.request<GenerationResponse>("GET", `/api/v1/generations/${id}`)
      if (ApiClient.TERMINAL.includes(generation.status)) return generation
      await new Promise((resolve) => setTimeout(resolve, 1500))
    }
    throw new Error("timeout")
  }

  async size(front: File, side: File | null, height: number,
             product: ProductContext, visitor: string,
             session: string): Promise<GenerationResponse> {
    const form = new FormData()
    form.append("front_image", front)
    form.append("consent", "true")
    if (side) form.append("side_image", side)
    form.append("height_cm", String(height))
    form.append("visitor", visitor)
    form.append("session", session)
    appendProduct(form, product)
    return this.createAndPoll("/api/v1/size_recommendation", form)
  }

  async resultImage(url: string): Promise<Blob> {
    const target = new URL(url, this.config.apiBase)
    if (target.origin !== new URL(this.config.apiBase).origin) {
      throw new Error("invalid_result_url")
    }
    return this.requestBlob(target.toString())
  }

  async deleteShopperPhotos(): Promise<void> {
    await this.request("DELETE", "/api/v1/shopper_photos")
  }

  track(eventName: string, product: ProductContext | null, extra: Record<string, unknown> = {}) {
    const json: Record<string, unknown> = {
      event_name: eventName, product: productPayload(product), ...extra
    }
    // Stamp outreach attribution (cold-email ?ref / UTM / referrer) onto the
    // funnel so each prospect's events are joinable. Only when present, so
    // organic visits add no noise.
    if (Object.keys(this.attribution).length > 0) {
      json.properties = { ...(extra.properties as Record<string, unknown> | undefined), ...this.attribution }
    }
    return this.request("POST", "/api/v1/events", { json }).catch(() => undefined)
  }

  private async createAndPoll(path: string, form: FormData,
                              onCreated?: (created: GenerationResponse) => void): Promise<GenerationResponse> {
    const created = await this.request<GenerationResponse>("POST", path, {
      form, idempotencyKey: crypto.randomUUID?.() || `${Date.now()}-${Math.random()}`
    })
    onCreated?.(created)
    return this.pollGeneration(created.id)
  }

  private async request<T = Record<string, unknown>>(
    method: string, path: string,
    body: { json?: unknown; form?: FormData; idempotencyKey?: string } = {}, retry = true
  ): Promise<T> {
    const headers: Record<string, string> = {
      "Authorization": `Bearer ${await this.shopperToken()}`
    }
    if (body.idempotencyKey) headers["Idempotency-Key"] = body.idempotencyKey
    let payload: BodyInit | undefined
    if (body.json) {
      headers["Content-Type"] = "application/json"
      payload = JSON.stringify(body.json)
    } else {
      payload = body.form
    }
    const response = await fetch(`${this.config.apiBase}${path}`, { method, headers, body: payload })
    const data = await response.json().catch(() => ({}))
    if (response.status === 401 && retry) {
      this.clearToken()
      return this.request<T>(method, path, body, false)
    }
    if (!response.ok) throw new Error(data.error || `http_${response.status}`)
    return data as T
  }

  // Boot handshake: which capabilities the cockpit has enabled for this merchant
  // (resolved from the request Origin, no token needed). Fetched before the modal
  // renders so the UI never offers a capability the backend would deny.
  async fetchConfig(): Promise<WidgetCapabilities> {
    const response = await fetch(`${this.config.apiBase}/api/v1/widget_config`, {
      headers: { "Accept": "application/json" }
    })
    if (!response.ok) throw new Error(`http_${response.status}`)
    const data = await response.json() as { capabilities?: WidgetCapabilities }
    return data.capabilities || {}
  }

  private shopperToken(): Promise<string> {
    if (this.token && Date.now() < this.tokenExpiresAt) return Promise.resolve(this.token)
    if (this.tokenRequest) return this.tokenRequest

    this.tokenRequest = this.openShopperSession().finally(() => { this.tokenRequest = null })
    return this.tokenRequest
  }

  private async requestBlob(url: string, retry = true): Promise<Blob> {
    const response = await fetch(url, {
      headers: { "Authorization": `Bearer ${await this.shopperToken()}` }
    })
    if (response.status === 401 && retry) {
      this.clearToken()
      return this.requestBlob(url, false)
    }
    if (!response.ok) {
      const data = await response.json().catch(() => ({})) as { error?: string }
      throw new Error(data.error || `http_${response.status}`)
    }
    return response.blob()
  }

  private async openShopperSession(): Promise<string> {
    // Origin-based tenancy: the backend resolves the merchant from the request
    // Origin (browser-set), so the call carries no tenant credential.
    const response = await fetch(`${this.config.apiBase}/api/v1/shopper_sessions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ visitor: this.visitor, session: this.session })
    })
    const data = await response.json().catch(() => ({})) as {
      token?: string
      expires_in?: number
      error?: string
    }
    if (!response.ok || !data.token) throw new Error(data.error || `http_${response.status}`)

    this.token = data.token
    const usableSeconds = Math.max(30, (data.expires_in || 600) - 30)
    this.tokenExpiresAt = Date.now() + usableSeconds * 1000
    return data.token
  }

  private clearToken() {
    this.token = null
    this.tokenExpiresAt = 0
  }
}

function appendProduct(form: FormData, product: ProductContext) {
  Object.entries(productPayload(product) || {}).forEach(([key, value]) => {
    if (value != null && value !== "") form.append(`product[${snakeCase(key)}]`, String(value))
  })
}

function productPayload(product: ProductContext | null): Record<string, unknown> | null {
  if (!product) return null
  return {
    external_id: product.externalId,
    name: product.name,
    image_url: product.imageUrl,
    canonical_url: product.canonicalUrl,
    description: product.description,
    category: product.category,
    currency: product.currency,
    price_cents: product.priceCents
  }
}

function snakeCase(value: string) {
  return value.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}
